import { isEmpty } from 'bold-ui'
import { SimNaoEnum } from 'components/form'
import { MChatPerguntaEnum } from 'graphql/types.generated'
import { cloneDeep } from 'lodash'
import { emptyArray } from 'util/array'

import {
  ConsultaSeguimentoAplicacaoItemModel,
  ConsultaSeguimentoAplicacaoModel,
  ConsultaSeguimentoPerguntaStatusEnum,
  ConsultaSeguimentoResultadoEnum,
  PassouFalhouEnum,
} from './model-consultaSeguimento'

export function calculateResultadoConsultaSeguimento(
  aplicacao: ConsultaSeguimentoAplicacaoModel
): ConsultaSeguimentoResultadoEnum {
  let resultado: ConsultaSeguimentoResultadoEnum
  const values = Object.values(aplicacao)
  const perguntasFalhou = values.filter((value) => value.resultado === PassouFalhouEnum.FALHOU)

  if (perguntasFalhou.length >= 2) {
    resultado = ConsultaSeguimentoResultadoEnum.RASTREAMENTO_POSITIVO
  } else {
    resultado = ConsultaSeguimentoResultadoEnum.RASTREAMENTO_NEGATIVO
  }

  return resultado
}

export function getPerguntasSelecionadasInitialStatus(
  perguntasSelecionadas: Readonly<MChatPerguntaEnum[]> = emptyArray
): ConsultaSeguimentoAplicacaoModel {
  if (isEmpty(perguntasSelecionadas)) return {}

  const map: ConsultaSeguimentoAplicacaoModel = Object.fromEntries(
    perguntasSelecionadas.map((pergunta): [MChatPerguntaEnum, ConsultaSeguimentoAplicacaoItemModel] => [
      pergunta,
      {
        status: ConsultaSeguimentoPerguntaStatusEnum.NAO_DISPONIVEL,
      },
    ])
  )
  map[perguntasSelecionadas[0]] = {
    status: ConsultaSeguimentoPerguntaStatusEnum.RESPONDENDO,
  }
  return map
}

export function isPerguntaStatusRespondendo(status: ConsultaSeguimentoPerguntaStatusEnum): boolean {
  return [
    ConsultaSeguimentoPerguntaStatusEnum.RESPONDENDO,
    ConsultaSeguimentoPerguntaStatusEnum.RESPONDENDO_FALHOU,
    ConsultaSeguimentoPerguntaStatusEnum.RESPONDENDO_PASSOU,
  ].includes(status)
}

export function getPerguntaAtiva(
  aplicacao: ConsultaSeguimentoAplicacaoModel
): {
  perguntaAtiva: MChatPerguntaEnum
  indexOfPerguntaAtiva: number
  isFirstPergunta: boolean
  isLastPergunta: boolean
} {
  const perguntas: MChatPerguntaEnum[] = Object.keys(aplicacao ?? {}) as MChatPerguntaEnum[]
  const perguntaAtiva: MChatPerguntaEnum = perguntas.find((pergunta) =>
    isPerguntaStatusRespondendo(aplicacao[pergunta].status)
  )
  const indexOfPerguntaAtiva = perguntas.indexOf(perguntaAtiva)
  const indexOfLastPergunta = perguntas.length - 1
  const isLastPergunta = indexOfPerguntaAtiva === indexOfLastPergunta
  const isFirstPergunta = indexOfPerguntaAtiva === 0

  return {
    perguntaAtiva,
    indexOfPerguntaAtiva,
    isFirstPergunta,
    isLastPergunta,
  }
}

export function getPerguntaNextStatus(
  status: ConsultaSeguimentoPerguntaStatusEnum
): ConsultaSeguimentoPerguntaStatusEnum {
  if (status === ConsultaSeguimentoPerguntaStatusEnum.RESPONDENDO_FALHOU) {
    return ConsultaSeguimentoPerguntaStatusEnum.FINALIZADO_FALHOU
  }
  if (status === ConsultaSeguimentoPerguntaStatusEnum.RESPONDENDO_PASSOU) {
    return ConsultaSeguimentoPerguntaStatusEnum.FINALIZADO_PASSOU
  }
  return null
}

export function getPerguntaPrevStatus(
  status: ConsultaSeguimentoPerguntaStatusEnum
): ConsultaSeguimentoPerguntaStatusEnum {
  if (
    status === ConsultaSeguimentoPerguntaStatusEnum.RESPONDENDO ||
    status === ConsultaSeguimentoPerguntaStatusEnum.DISPONIVEL
  ) {
    return ConsultaSeguimentoPerguntaStatusEnum.DISPONIVEL
  }
  if (status === ConsultaSeguimentoPerguntaStatusEnum.RESPONDENDO_FALHOU) {
    return ConsultaSeguimentoPerguntaStatusEnum.FINALIZADO_FALHOU
  }
  if (status === ConsultaSeguimentoPerguntaStatusEnum.FINALIZADO_FALHOU) {
    return ConsultaSeguimentoPerguntaStatusEnum.RESPONDENDO_FALHOU
  }
  if (status === ConsultaSeguimentoPerguntaStatusEnum.RESPONDENDO_PASSOU) {
    return ConsultaSeguimentoPerguntaStatusEnum.FINALIZADO_PASSOU
  }
  if (status === ConsultaSeguimentoPerguntaStatusEnum.FINALIZADO_PASSOU) {
    return ConsultaSeguimentoPerguntaStatusEnum.RESPONDENDO_PASSOU
  }
  return ConsultaSeguimentoPerguntaStatusEnum.NAO_DISPONIVEL
}

export function getAplicacaoWithLastQuestionAsPrevStatus(aplicacao: ConsultaSeguimentoAplicacaoModel) {
  const clone = cloneDeep(aplicacao)
  const perguntas: MChatPerguntaEnum[] = Object.keys(aplicacao) as MChatPerguntaEnum[]
  const last: MChatPerguntaEnum = perguntas[perguntas.length - 1]

  clone[last] = {
    ...clone[last],
    status: getPerguntaPrevStatus(clone[last].status),
  }

  return clone
}

export function getAplicacaoWithLastQuestionAsNextStatus(
  aplicacao: ConsultaSeguimentoAplicacaoModel
): ConsultaSeguimentoAplicacaoModel {
  const clone = cloneDeep(aplicacao)
  const perguntas: MChatPerguntaEnum[] = Object.keys(aplicacao) as MChatPerguntaEnum[]
  const last: MChatPerguntaEnum = perguntas[perguntas.length - 1]

  clone[last] = {
    ...clone[last],
    status: getPerguntaNextStatus(clone[last].status),
  }

  return clone
}

export const hasAnyNao = <T extends string>(value: Record<T, SimNaoEnum>): boolean => {
  if (isEmpty(value)) return false
  return Object.values(value).some((item) => item === SimNaoEnum.NAO)
}

export const hasAnySim = <T extends string>(value: Record<T, SimNaoEnum>): boolean => {
  if (isEmpty(value)) return false
  return Object.values(value).some((item) => item === SimNaoEnum.SIM)
}

export const isAllNao = <T extends string>(value: Record<T, SimNaoEnum>, expected: T[]): boolean => {
  if (!value || !expected || isEmpty(expected)) return false

  return expected.every((campo) => value[campo] === SimNaoEnum.NAO)
}

export const isAllFilled = <T extends string>(value: Record<T, SimNaoEnum>, expected: T[]): boolean => {
  if (!value || !expected || isEmpty(expected)) return false

  return expected.every((campo) => value[campo] === SimNaoEnum.NAO || value[campo] === SimNaoEnum.SIM)
}
